import type { AlertFrequencySeconds } from '../types';

/**
 * AlertFrequency Value Object
 *
 * Domain concept: "How often should we notify the user about new listings
 * matching a saved search?"
 *
 * Business rules:
 * - Frequency is stored as a second-count: 0 (off), -1 (instant), 86400 (daily),
 *   604800 (weekly). These mirror `NotificationFrequency` in utils/select-options.
 * - The default for a brand-new search is Daily.
 * - An unknown/out-of-range persisted value normalizes to Off — we never silently
 *   start alerting on data we don't understand.
 *
 * Immutable. Construct via the static factories.
 *
 * Usage:
 * - Draft/record: src/domain/saved-search/saved-search-draft.ts, saved-search-record.ts
 * - UI mapping (VO ↔ SelectOption) happens in the hook/component layer.
 */
const OFF_SECONDS = 0 as const;
const INSTANT_SECONDS = -1 as const;
const DAILY_SECONDS = 86400 as const;
const WEEKLY_SECONDS = 604800 as const;

const KNOWN_SECONDS: readonly AlertFrequencySeconds[] = [
  OFF_SECONDS,
  INSTANT_SECONDS,
  DAILY_SECONDS,
  WEEKLY_SECONDS,
];

export class AlertFrequency {
  readonly #seconds: AlertFrequencySeconds;

  private constructor(seconds: AlertFrequencySeconds) {
    this.#seconds = seconds;
  }

  static readonly OFF = new AlertFrequency(OFF_SECONDS);
  static readonly INSTANT = new AlertFrequency(INSTANT_SECONDS);
  static readonly DAILY = new AlertFrequency(DAILY_SECONDS);
  static readonly WEEKLY = new AlertFrequency(WEEKLY_SECONDS);

  /** The frequency applied to a brand-new saved search when none is chosen. */
  static readonly DEFAULT = AlertFrequency.DAILY;

  /**
   * Creates an AlertFrequency from a raw second-count.
   * Unknown/out-of-range values normalize to `OFF` (never auto-enable alerts).
   */
  static from(seconds: number | null | undefined): AlertFrequency {
    return KNOWN_SECONDS.includes(seconds as AlertFrequencySeconds)
      ? new AlertFrequency(seconds as AlertFrequencySeconds)
      : AlertFrequency.OFF;
  }

  /** The raw second-count for persistence (`SavedSearch.frequency`). */
  get seconds(): AlertFrequencySeconds {
    return this.#seconds;
  }

  get isOff(): boolean {
    return this.#seconds === OFF_SECONDS;
  }

  get isInstant(): boolean {
    return this.#seconds === INSTANT_SECONDS;
  }

  get isDaily(): boolean {
    return this.#seconds === DAILY_SECONDS;
  }

  get isWeekly(): boolean {
    return this.#seconds === WEEKLY_SECONDS;
  }

  equals(other: AlertFrequency): boolean {
    return this.#seconds === other.seconds;
  }
}
